import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  chartToIcon,
  chartToString,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { cn } from '@/shared/utils/cn';

export const ChartTypeSelector = ({
  currChartType,
  onUpdateChartType,
  availableChartTypes,
}: {
  currChartType: ChartType;
  onUpdateChartType?: (chartType: ChartType) => void;
  availableChartTypes?: ChartType[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('chart', { keyPrefix: 'chart_types' });

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        className={
          availableChartTypes && onUpdateChartType
            ? 'cursor-pointer'
            : 'cursor-default'
        }
        asChild
      >
        <Button
          variant="outline"
          btnType="secondary"
          className={cn('border-white/30')}
        >
          {chartToIcon(currChartType)}
          {chartToString(currChartType, t)}
        </Button>
      </DropdownMenuTrigger>
      {availableChartTypes && (
        <DropdownMenuContent
          side="top"
          align="end"
          sideOffset={12}
          alignOffset={-7}
        >
          <div className="mx-4 my-2 flex flex-row items-center">
            <DropdownMenuLabel className="flex-1 p-0 text-xs font-normal">
              Charttype
            </DropdownMenuLabel>
            <XIcon
              className="cursor-pointer text-white"
              size={'15px'}
              onClick={() => setIsOpen(false)}
            />
          </div>
          <DropdownMenuSeparator className="my-2 h-[0.75px]" />
          <RadioGroup value={currChartType} onValueChange={onUpdateChartType}>
            <div className="mx-3 my-3 flex flex-row justify-evenly gap-6">
              {availableChartTypes.map((chartType) => (
                <div
                  key={chartType}
                  className="flex flex-col items-center gap-4"
                >
                  <RadioGroupItem value={chartType} id={chartType} />
                  <Label htmlFor={chartType}>
                    <div className="flex flex-row items-center gap-2">
                      {chartToIcon(chartType)}
                      {chartToString(chartType, t)}
                    </div>
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};
